import React, { useRef, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'

export const AccountDropdown: React.FC = () => {
  const { isAuthenticated, user, clearAuth } = useAuthStore()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  const handleLogout = () => {
    clearAuth()
    setOpen(false)
    navigate('/')
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-3">
        <Link
          to="/login"
          className="
            text-body-base font-semibold text-charcoal
            hover:text-royal-orange transition-colors duration-150
            focus-visible:outline-none focus-visible:underline
          "
        >
          Login
        </Link>
        <Link
          to="/register"
          className="
            px-4 py-2 bg-royal-orange hover:bg-royal-orange-hover
            text-white font-semibold text-body-base rounded-sm
            transition-all duration-150
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange focus-visible:ring-offset-2
          "
        >
          Register
        </Link>
      </div>
    )
  }

  // Authenticated
  const initials = user?.full_name
    ? user.full_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : '?'

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="account-menu"
        aria-label={`Account menu for ${user?.full_name ?? 'user'}`}
        className="
          flex items-center gap-2
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange rounded-sm
        "
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-burgundy text-white flex items-center justify-center font-sans font-bold text-caption shrink-0" aria-hidden="true">
          {(user as any)?.avatar ? (
            <img src={(user as any).avatar} alt="" className="w-full h-full object-cover rounded-full" />
          ) : (
            initials
          )}
        </div>
        <span className="hidden lg:block text-body-base font-semibold text-charcoal max-w-[120px] truncate">
          {user?.full_name?.split(' ')[0]}
        </span>
        <svg
          className={`w-3.5 h-3.5 text-dark-gray transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          id="account-menu"
          role="menu"
          aria-label="Account options"
          className="
            absolute right-0 top-full mt-2
            w-52 bg-white border border-mid-gray rounded-sm shadow-elevation-3
            py-1 z-50 animate-fade-in
          "
        >
          {/* User info */}
          <div className="px-4 py-3 border-b border-mid-gray">
            <p className="text-body-base font-semibold text-charcoal truncate">{user?.full_name}</p>
            <p className="text-caption text-dark-gray truncate">{user?.email}</p>
          </div>

          {/* Links */}
          <nav>
            <DropdownLink to="/account" onClick={() => setOpen(false)} label="My Account" icon="user" />
            <DropdownLink to="/account/orders" onClick={() => setOpen(false)} label="Orders" icon="package" />
            <DropdownLink to="/account/wishlist" onClick={() => setOpen(false)} label="Wishlist" icon="heart" />

            {/* Admin link — role-gated */}
            {user?.role === 'admin' && (
              <>
                <div className="border-t border-mid-gray my-1" />
                <DropdownLink to="/dashboard" onClick={() => setOpen(false)} label="Admin Dashboard" icon="shield" />
              </>
            )}
          </nav>

          <div className="border-t border-mid-gray mt-1" />
          <button
            role="menuitem"
            onClick={handleLogout}
            className="
              w-full flex items-center gap-3 px-4 py-2.5
              text-body-base text-error-red font-semibold
              hover:bg-light-gray transition-colors duration-100
              focus-visible:outline-none focus-visible:bg-light-gray
            "
          >
            <LogoutIcon />
            Sign Out
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const ICONS = {
  user: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  package: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  heart: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  shield: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
}

const LogoutIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

interface DropdownLinkProps {
  to: string
  label: string
  icon: keyof typeof ICONS
  onClick: () => void
}

const DropdownLink: React.FC<DropdownLinkProps> = ({ to, label, icon, onClick }) => (
  <Link
    to={to}
    role="menuitem"
    onClick={onClick}
    className="
      flex items-center gap-3 px-4 py-2.5
      text-body-base text-charcoal
      hover:bg-light-gray hover:text-royal-orange transition-colors duration-100
      focus-visible:outline-none focus-visible:bg-light-gray
    "
  >
    <span className="text-dark-gray">{ICONS[icon]}</span>
    {label}
  </Link>
)